import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from "react-intl";

import SelectTitle from "components/radioGroup/SelectTitle";
import StyledReactSelect from "components/select/StyledReactSelect";

import {getProductFields} from "utils/mapperFields";
import {capitalize} from "utils/capitalize";
import decapitalize from "utils/decapitalize";
import isObject from "utils/isObject";
import listOptions from "api/option/list";
import {getTranslation} from "domain/helpers/translations";

const OPTION_PREFIX = 'option_';

FeedMasterFields.propTypes = {
    feedMasterFields: PropTypes.object,
    actualFeed: PropTypes.object,
    onChange: PropTypes.func
};

function FeedMasterFields({actualFeed, feedMasterFields, onChange, ...props}) {
    const productFields = getProductFields();
    const [selectedFields, setSelectedFields] = React.useState([]);
    const [organizationOptions, setOrganizationOptions] = React.useState([]);

    const productFieldOptions = productFields.map(item => ({
        label: <FormattedMessage id={`feed.form.label.product.${item}`} />,
        value: item === 'pictures' ? 'variationPictures' : `product${capitalize(item)}` // externalId -> productExternalId, pictures -> variationPictures
    }));

    const optionFieldOptions = organizationOptions.map(option => ({
        label: <FormattedMessage id="feed.form.label.feedMasterFields.option" values={{name: getTranslation(option).name || option.code}} />,
        value: `${OPTION_PREFIX}${option.id}`
    }));

    const options = [...productFieldOptions, ...optionFieldOptions];

    useEffect(() => {
        listOptions('order[position]=asc').then((response) => {
            setOrganizationOptions(response);
        });
    }, []);

    useEffect(() => {
        if (isObject(feedMasterFields)) {
            setSelectedFields(formatInitialValues(feedMasterFields));
        }
    }, [feedMasterFields, organizationOptions])

    function formatInitialValues(values) {
        const fields = Object.entries(values)
            .filter(([key, value]) => !['@id', 'id', '@type', 'options'].includes(key) && value !== false)
            .map(([key]) => ({
                label: key === 'variationPictures' ? 'pictures' : decapitalize(key.replace(/^product/, '')),
                value: key
            }));

        // Add selected organization options
        if (Array.isArray(values.options)) {
            values.options.forEach(optionId => {
                const option = organizationOptions.find(o => o.id === optionId);
                if (option) {
                    fields.push({
                        label: <FormattedMessage id="feed.form.label.feedMasterFields.option" values={{name: getTranslation(option).name || option.code}} />,
                        value: `${OPTION_PREFIX}${optionId}`
                    });
                }
            });
        }

        return fields;
    }

    function flatSelectedFields(fields) {
        if (!fields) {
            return null;
        }

        const values = isObject(feedMasterFields) ? feedMasterFields : {}

        // set all product fields to false by default
        productFields.forEach(item => {
            const key = item === 'pictures' ? 'variationPictures' : `product${capitalize(item)}`;
            values[key] = false;
        })

        // reset options
        values.options = [];

        fields.forEach(item => {
            if (item.value.startsWith(OPTION_PREFIX)) {
                // Extract option ID from the prefixed value
                values.options.push(item.value.substring(OPTION_PREFIX.length));
            } else {
                values[item.value] = true;
            }
        })

        values.feed = actualFeed;

        return values;
    }

    return (
        <>
            <SelectTitle titleTranslationKey='feed.form.label.feedMasterFields' />
            <StyledReactSelect
                isMulti
                closeMenuOnSelect={false}
                placeholder={<FormattedMessage id='feed.form.label.feedMasterFields' />}
                name={'feedMasterFields'}
                value={selectedFields}
                options={options}
                onChange={(value) => {
                    setSelectedFields(value);
                    onChange(flatSelectedFields(value));
                }}
            />
        </>
    );
}

export default FeedMasterFields;